import { useLocation, useRoute } from "wouter";
import { useAuth } from "../../auth/AuthContext";
import { canSeeFleet } from "../../auth/roleView";
import { EmptyState } from "../../components/AsyncStates";
import { AuditPage } from "../admin/audit/AuditPage";
import { GovernanceDecisionsPage } from "../governance/GovernanceDecisionsPage";
import { TabPanel, Tabs, type TabItem } from "../../components/Tabs";
import { CredentialsHome } from "./CredentialsHome";
import { PolicyTab } from "./PolicyTab";
import "./govern.css";

/**
 * Govern's home (IA slice 5, docs/product/ui-information-architecture.md §6): "the
 * productization of our whole trust story" — the surface a Platform Lead uses on a Tuesday
 * and shows the auditor in March, organized around its three strands: THE RECORD (Audit +
 * Decisions — what happened), THE RULES (Policy — what's allowed, read-only, every row
 * linking to its one real editing surface), and THE CUSTODY (Credentials — what the platform
 * holds in trust). Mounted at `/govern` (renamed from `/admin/audit` in IA slice 6 — a
 * clean pre-beta rename, no alias kept, see App.tsx) plus sibling tab routes — "tab and
 * route are the same thing", same pattern as `ProjectDetailPage` (IA slice 3).
 *
 * Every tab mounts an existing, unchanged page component (AuditPage, GovernanceDecisionsPage,
 * CredentialsHome) rather than a second copy of their query/mutation logic (A-5) — this
 * component only owns which tab is active, the route it lives at, and — per-TAB, not
 * per-page — which tabs a role can even see (hidden, not greyed, S-8: this mirrors the
 * server's authz matrix, `server/internal/authz/model.go`, it never re-decides it):
 *
 *   - Audit / Decisions / Policy read audit-log- and governance-scoped data — admin-only
 *     surfaces (`ResAuditLog`/governance decisions are admin-gated server-side; `AuditPage`/
 *     `GovernanceDecisionsPage` both wrap themselves in `AdminGate` too, belt-and-suspenders).
 *   - Credentials is visible to EVERY fleet role, not just admin: the matrix grants `:view`
 *     on all three credential kinds to viewer (`allow(RoleViewer, ResSecret, ActView)` etc,
 *     model.go:919/943/947), and operator additionally holds FULL secret CRUD
 *     (`allow(RoleOperator, ResSecret, ActView, ActCreate, ActUpdate, ActDelete)`,
 *     model.go:982 — "set/rotate/delete" is an operator job). Gating the whole Credentials
 *     tab to admin-only — as an earlier version of this component did — locked operators out
 *     of a capability they legitimately hold; that was the bug (secrets used to be an
 *     un-gated top-level route, server-enforced). Provider/LLM credential MUTATIONS stay
 *     admin-only (model.go:1023/1030 — operator holds only `ActView`/`ActExec` on those two,
 *     never create/update/delete) — that's already enforced inside
 *     `ProviderCredentialsPage`/`LlmCredentialsPage`'s own `isAdmin` checks, unchanged here.
 *
 * A role with at least one visible tab always lands on one (falling back to its first visible
 * tab if the routed tab isn't one it can see, e.g. an operator hitting `/govern` itself
 * lands on Credentials, not a blank Audit panel). A role with NO visible tab at all (today:
 * only `billing_admin`, which the nav never even links here for) sees one honest "no access"
 * message instead of an empty tablist.
 */
export function GovernPage() {
  const { session } = useAuth();
  const [, navigate] = useLocation();
  const [matchDecisions] = useRoute("/govern/decisions");
  const [matchPolicy] = useRoute("/govern/policy");
  const [matchCredentials] = useRoute("/govern/credentials");
  const [matchCredentialsProvider] = useRoute("/govern/credentials/provider");
  const [matchCredentialsLlm] = useRoute("/govern/credentials/llm");
  const routedTab = matchDecisions
    ? "decisions"
    : matchPolicy
      ? "policy"
      : matchCredentials || matchCredentialsProvider || matchCredentialsLlm
        ? "credentials"
        : "audit";

  const isAdmin = session?.role === "admin";
  const showRecordAndPolicy = isAdmin;
  // Every fleet role holds at least metadata-view on every credential kind (see doc comment
  // above) — `canSeeFleet` (admin/operator/viewer) is the exact same set, reused rather than
  // hand-rolled here (A-5).
  const showCredentials = canSeeFleet(session?.role);

  const tabItems: TabItem[] = [
    ...(showRecordAndPolicy
      ? [
          { id: "audit", label: "Audit" },
          { id: "decisions", label: "Decisions" },
          { id: "policy", label: "Policy" },
        ]
      : []),
    ...(showCredentials ? [{ id: "credentials", label: "Credentials" }] : []),
  ];

  if (tabItems.length === 0) {
    return (
      <EmptyState
        title="No access"
        description="This area is limited to roles with fleet or governance grants. Ask an admin if you need access."
      />
    );
  }

  // Falls back to the first visible tab rather than rendering a route match
  // (`routedTab`) the role can't see — e.g. an operator hitting the base
  // `/govern` route lands on Credentials, its only visible tab, not a
  // blank Audit panel it's about to get redirected away from.
  const activeTab = tabItems.some((t) => t.id === routedTab) ? routedTab : tabItems[0].id;

  function handleTabChange(id: string) {
    navigate(id === "audit" ? "/govern" : `/govern/${id}`);
  }

  return (
    <div className="govern-page">
      <h1 className="visually-hidden">Govern</h1>
      <div className="govern-page__tabs">
        <Tabs items={tabItems} activeId={activeTab} onChange={handleTabChange} ariaLabel="Govern views" />
      </div>

      {showRecordAndPolicy && (
        <>
          <TabPanel id="audit" activeId={activeTab}>
            <AuditPage />
          </TabPanel>
          <TabPanel id="decisions" activeId={activeTab}>
            <GovernanceDecisionsPage />
          </TabPanel>
          <TabPanel id="policy" activeId={activeTab}>
            <PolicyTab />
          </TabPanel>
        </>
      )}
      {showCredentials && (
        <TabPanel id="credentials" activeId={activeTab}>
          <CredentialsHome />
        </TabPanel>
      )}
    </div>
  );
}
